/**
 * Subscription consent checkbox.
 *
 * Country-aware copy (mirrors `TermsCheckbox`):
 *   - CA: dual English / French wording with two subscription-terms links.
 *   - Anywhere else: single English "I agree …" line.
 *
 * Only rendered by the checkout flow when `paymentType === 'recurring'`
 * and the country is not UK (per the SWE-1206 spec).
 */

import React from 'react';
import { Box, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';

import { COUNTRY_CODES, SUBSCRIPTION_CONTENT, UI_CONFIG } from '../../utils/constants';
import { getSubscriptionTermsByCountry, getSubscriptionTermsUrl } from '../../utils/redirect';

export interface SubscriptionConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  country: string;
}

const SubscriptionConsent: React.FC<SubscriptionConsentProps> = ({ checked, onChange, country }) => {
  const upper = country?.toUpperCase();
  const termsUrl = getSubscriptionTermsUrl(country);
  const formControlLabelSx = {
    alignItems: 'flex-start',
    m: 0,
    width: '100%',
    '.MuiCheckbox-root': {
      alignSelf: 'flex-start',
      mr: 1.5,
      p: 0,
      pt: '2px',
    },
    '.MuiFormControlLabel-label': {
      flex: 1,
      display: 'block',
      mt: '2px',
      textAlign: 'justify',
      textJustify: 'inter-word',
    },
  } as const;

  const checkbox = (
    <Checkbox
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      sx={{
        color: 'white',
        '&.MuiCheckbox-root.Mui-checked': { color: 'white' },
      }}
    />
  );

  // CA renders both English and French consent lines, matching the bilingual
  // TermsCheckbox treatment (and the legacy PaymentForm subscription block).
  if (upper === COUNTRY_CODES.CA) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
          sx={formControlLabelSx}
          control={checkbox}
          label={
            <Box
              component="span"
              sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, fontSize: '0.87rem' }}
            >
              {SUBSCRIPTION_CONTENT.AGREEMENT_TEXT}
              {' '}
              <Link
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
              >
                {SUBSCRIPTION_CONTENT.SUBSCRIPTION_TERMS_TEXT}
              </Link>
              {' '}
              {SUBSCRIPTION_CONTENT.CONTINUES_TEXT}
              <br />
              <br />
              {SUBSCRIPTION_CONTENT.AGREEMENT_TEXT_FR}
              {' '}
              <Link
                href={getSubscriptionTermsByCountry.CA_FR}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
              >
                {SUBSCRIPTION_CONTENT.SUBSCRIPTION_TERMS_TEXT_FR}
              </Link>
              {' '}
              {SUBSCRIPTION_CONTENT.CONTINUES_TEXT_FR}
            </Box>
          }
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <FormControlLabel
        sx={formControlLabelSx}
        control={checkbox}
        label={
          <Typography
            component="span"
            sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, fontSize: '0.87rem' }}
          >
            {SUBSCRIPTION_CONTENT.AGREEMENT_TEXT}
            {' '}
            <Link
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, textDecoration: 'underline' }}
            >
              {SUBSCRIPTION_CONTENT.SUBSCRIPTION_TERMS_TEXT}
            </Link>
            {' '}
            {SUBSCRIPTION_CONTENT.CONTINUES_TEXT}
          </Typography>
        }
      />
    </Box>
  );
};

export default SubscriptionConsent;
